<template>
  <div class="mx-auto p-4 w-full max-w-7xl">
    <div
      v-if="imageUrl"
      class="hero-image bg-cover bg-center relative rounded w-full mb-10 mt-4 transition-all duration-300 ease-in-out"
      :style="{ 'background-image': `url(${imageUrl})` }"
    />
    <div class="flex mt-4 text-base">
      <article class="flex-1">
        <div class="flex items-center">
          <h1 class="heading-1 mb-2">{{ programDisplayHeading }}</h1>
          <div class="flex-grow"></div>
          <span
            v-if="enablePrintDocuments && program.id"
            class="link text-underline flex items-center"
            @click="() => downloadDocument(program.id)"
          >
            <BaseIcon
              v-if="isDownloadingDocument"
              icon="spinner"
              class="spinner inline-block mr-2"
            />
            <h2>Download as PDF</h2>
          </span>
        </div>
        <div class="text-small mb-10">
          <template v-if="!hideDepartments">
            <NuxtLink
              v-for="department of programDepartments"
              :key="department.id"
              :to="`/departments/${encodeURIComponent(department.id)}/overview`"
            >
              {{ department.displayName || department.name }}
            </NuxtLink>
            <BaseIcon
              v-if="program.level"
              icon="circle"
              class="mx-4 icon-sm"
            />
          </template>
          <span>{{ program.level }}</span>
          <BaseIcon
            v-if="program.degreeDesignation"
            icon="circle"
            class="mx-4 icon-sm"
          />
          <span>{{ program.degreeDesignation }}</span>
          <RevisionSelector
            v-if="program.programGroupId"
            class="ml-4 inline-flex items-center"
            entity-type="programs"
            :entity-id="program.programGroupId"
          />
        </div>
        <HorizontalScroll
          v-if="!shouldHideTabs"
          class="border-b"
          :hide-arrows="availableTabs.length <= 1"
        >
          <BaseTabs
            :tabs="availableTabs"
            :value="activeTab"
            @input="activeTabKey = $event.key"
          >
            <template #default="{ tab, isActive }">
              <h2
                class="inline tab-label text-black font-bold pb-2 transition-all duration-300 ease-in-out border-transparent"
                :class="{ 'tab-active heading-5 border-black': isActive }"
              >
                {{ tab.label }}
              </h2>
            </template>
          </BaseTabs>
        </HorizontalScroll>
        <section :id="`${activeTemplateCard.id}TabContent`">
          <transition name="fade-in">
            <DynamicFieldsRenderer
              v-if="fieldRows.length"
              :key="activeTemplateCard.id"
              class="mt-4 mb-2 -mx-2"
              :fields="fieldRows"
              :questions="questions"
              :document-value="program"
              :value="program"
              entity-type="program"
            />
            <EmptyState
              v-else
              :key="`${activeTemplateCard.id}-empty`"
              class="bg-gray-50 rounded p-16 my-4"
            />
          </transition>
        </section>
      </article>
      <slot name="sidebar" />
    </div>
  </div>
</template>

<script>
import { useContext, computed, toRefs } from '@nuxtjs/composition-api'
import { mapState } from 'vuex'
import DynamicFieldsRenderer from '@/components/contemporaryTheme/components/DynamicFieldsRenderer'
import EmptyState from '@/components/contemporaryTheme/components/Base/EmptyState'
import { isObject, anyCardItemVisible } from '@/utils/generalUtils'
import HorizontalScroll from '@/components/HorizontalScroll'
import useFeatureFlags from '@/composables/useFeatureFlags'
import {
  TITLE_INTERPOLATIONS,
  HIDE_EMPTY_CARDS,
  HIDE_DEPARTMENTS,
  PRINT_DOCUMENTS,
} from '@/constants/featureFlags'
import useDocumentsDownload from '@/composables/useDocumentsDownload'
import { programNameWithFallbacks } from '@/utils/entityNames'
import { generateProgramLabel } from '@/utils/dataLabels'
import useInterpolation from '@/composables/useInterpolation'
import RevisionSelector from '@/components/RevisionSelector'

export default {
  components: {
    DynamicFieldsRenderer,
    EmptyState,
    HorizontalScroll,
    RevisionSelector
  },
  props: {
    program: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const { program } = toRefs(props)
    const { store } = useContext()
    const {
      hideEmptyCards,
      hideDepartments,
      enableTitleInterpolations,
      enablePrintDocuments,
    } = useFeatureFlags({
      hideEmptyCards: HIDE_EMPTY_CARDS,
      hideDepartments: HIDE_DEPARTMENTS,
      enableTitleInterpolations: TITLE_INTERPOLATIONS,
      enablePrintDocuments: PRINT_DOCUMENTS,
    })
    const { getInterpolatedFieldValue } = useInterpolation()
    const { downloadDocument, isDownloadingDocument } = useDocumentsDownload(
      'program'
    )

    const programPageTemplate = computed(
      () => store.state.settings.programPageTemplate
    )
    const questions = Object.values(programPageTemplate.value.questions)
    const anyChildVisible = computed(() => anyCardItemVisible(questions, program.value))
    return {
      hideEmptyCards,
      questions,
      programPageTemplate,
      anyChildVisible,
      programNameWithFallbacks,
      hideDepartments,
      getInterpolatedFieldValue,
      enableTitleInterpolations,
      enablePrintDocuments,
      downloadDocument,
      isDownloadingDocument,
    }
  },
  data() {
    return {
      activeTabKey: null,
    }
  },
  computed: {
    ...mapState('departments', { departments: (state) => state.all }),
    ...mapState('settings', ['school']),
    activeTab() {
      return this.tabs.find((t) => t.key === this.activeTabKey) || this.tabs[0]
    },
    availableTabs() {
      return this.programPageTemplate.template
        .filter(
          (card) =>
            this.hideEmptyCards ? this.anyChildVisible(card.children) : true
        )
        .map((card) => ({
          key: card.id,
          label: card.config.title,
        }))
    },
    tabs() {
      return this.programPageTemplate.template.map((card) => ({
        key: card.id,
        label: card.config.title,
      }))
    },
    shouldHideTabs () {
      return this.tabs.length <= 1 && this.tabs.every(tab => !tab.label)
    },
    activeTemplateCard() {
      return this.programPageTemplate.template.find(
        (card) => card.id === this.activeTab.key
      )
    },
    fieldRows() {
      return this.activeTemplateCard.children.reduce((acc, row) => {
        acc.push([...row.children])
        return acc
      }, [])
    },
    programDepartments() {
      return (this.program.departments || []).map(
        (dep) =>
          typeof dep === 'object'
            ? dep
            : this.departments.find((el) => el.id === dep) || {
                id: dep,
                displayName: dep,
              }
      )
    },
    imageUrl() {
      return isObject(this.program.catalogImageUrl)
        ? this.program.catalogImageUrl.url
        : this.program.catalogImageUrl
    },
    programDisplayHeading() {
      return generateProgramLabel(this.program, this.school, {
        getInterpolatedFieldValue: this.enableTitleInterpolations
          ? this.getInterpolatedFieldValue
          : undefined,
      })
    },
  },
}
</script>

<style scoped lang="scss">
@import '@/assets/css/contemporaryTheme/tabbedTemplate.scss';
</style>
